import { Box } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import BreakdownBars from "../components/BreakdownBars";
import Card, { CardGrid } from "../components/Card";
import KpiTile from "../components/KpiTile";
import SeriesChart from "../components/SeriesChart";
import StatTable from "../components/StatTable";
import StatsGate from "../components/StatsGate";
import { formatPercent } from "../format";
import { Audience, TabProps } from "../types";
import { useStats } from "../useStats";

type CohortRow = { weekStart: string; size: number } & Record<string, string | number>;

export default function AudienceTab({ query, reloadKey, gameId }: TabProps) {
  const state = useStats<Audience>("/audience", query, reloadKey);
  // New fans are not scoped to a single game, so say so once a game id filters everything else.
  const newFansLabel = gameId ? "New fans (all games)" : "New fans";
  return (
    <StatsGate state={state}>
      {(data, envelope) => {
        const weeks = data.cohorts.reduce((m, c) => Math.max(m, c.retention.length), 0);
        const cohortRows: CohortRow[] = data.cohorts.map((c) => {
          const row: CohortRow = { weekStart: new Date(c.weekStart).toLocaleDateString(), size: c.size };
          c.retention.forEach((share, k) => { row[`w${k}`] = formatPercent(share); });
          return row;
        });
        const cohortColumns: GridColDef<CohortRow>[] = [
          { field: "weekStart", headerName: "Week of", flex: 1 },
          { field: "size", headerName: newFansLabel, type: "number", flex: 1 },
          ...Array.from({ length: weeks }, (_, k) => ({ field: `w${k}`, headerName: `Week ${k}`, flex: 1 })),
        ];
        return (
          <>
            <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", sm: "repeat(4, 1fr)" }, mb: 2 }}>
              <KpiTile label="Unique active fans" value={data.uniqueActive} />
              <KpiTile label="Stickiness (daily / range)" value={data.stickiness} format={formatPercent} />
              <KpiTile label="Banned now" value={data.bannedNow} />
              <KpiTile label="Muted now" value={data.mutedNow} />
            </Box>
            <CardGrid>
              <Card title="New and active fans" wide>
                <SeriesChart points={data.series} bucket={envelope.filters.bucket ?? "day"}
                  lines={[{ key: "activeUsers", label: "Active fans" }, { key: "newUsers", label: newFansLabel }]} />
              </Card>
              <Card title="Devices (fans)"><BreakdownBars rows={data.deviceMix} /></Card>
              <Card title="Roles (all time)"><BreakdownBars rows={data.roles} /></Card>
              <Card title="Weekly retention" wide>
                <StatTable rows={cohortRows} columns={cohortColumns} getRowId={(r) => String(r.weekStart)} />
              </Card>
            </CardGrid>
          </>
        );
      }}
    </StatsGate>
  );
}
